import { useCallback, useEffect, useState } from 'react';
import { useRequestInitialInstruction } from '../../../api/mutations/selfService/useRequestInitialInstruction';
import { useRequestNextInstruction } from '../../../api/mutations/selfService/useRequestNextInstruction';
import { SelfServiceSessionService } from '../../../api/generated';
import { useNavigate } from 'react-router-dom';
import {
  clearLocalStorageInstruction,
  readLocalStorageInstruction,
  selfServiceRoutes,
  setLocalStorageInstruction,
} from '../utils';
import { SelfServiceInstruction } from '../../../api/mutations/selfService/types';
import { useTranslation } from 'react-i18next';
import { selfServiceSessionKeys } from '../../../api/queries/selfService/selfServiceSession';
import { useQueryClient } from '@tanstack/react-query';

export function useComponentManager(jobsiteId: string, sessionId: string) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const queryClient = useQueryClient();

  const { mutateAsync: requestInitialInstruction } = useRequestInitialInstruction();
  const { mutateAsync: requestNextInstruction } = useRequestNextInstruction();

  const [instruction, setInstruction] = useState<SelfServiceInstruction>();

  const handleNextInstructionRequest = async (updatedInstruction: SelfServiceInstruction) => {
    if (!instruction) return;

    try {
      const nextInstruction = await requestNextInstruction({
        jobsiteId,
        sessionId,
        lang: currentLanguage,
        currentInstruction: updatedInstruction,
      });

      if (nextInstruction.component === 'success') {
        const eventResponse = await SelfServiceSessionService.selfServiceSessionControllerProcessSessionEvent({
          path: { jobsiteId, sessionId },
          body: {
            type: 'completed',
          },
        });

        if (eventResponse.error) {
          throw new Error('Fehler beim Abschließen der Sitzung');
        }

        await queryClient.invalidateQueries({
          queryKey: selfServiceSessionKeys.read({ jobsiteId, sessionId }),
        });

        clearLocalStorageInstruction(jobsiteId);
        navigate(selfServiceRoutes.sessionInfopoint(jobsiteId, sessionId));
      } else {
        setLocalStorageInstruction(nextInstruction, jobsiteId, sessionId);
      }

      setInstruction(nextInstruction);
    } catch (error) {
      handleApiError();
      throw error;
    }
  };

  const handleApiError = useCallback(async () => {
    clearLocalStorageInstruction(jobsiteId);
    await SelfServiceSessionService.selfServiceSessionControllerProcessSessionEvent({
      path: { jobsiteId, sessionId },
      body: {
        type: 'api-error',
      },
    });
    navigate(selfServiceRoutes.sessionError(jobsiteId));
  }, [jobsiteId, navigate, sessionId]);

  useEffect(() => {
    async function fetchData() {
      try {
        const savedInstruction = readLocalStorageInstruction(jobsiteId, sessionId);
        let initialInstruction;

        if (savedInstruction) {
          initialInstruction = savedInstruction;
        } else {
          initialInstruction = await requestInitialInstruction({
            jobsiteId,
            sessionId,
            lang: currentLanguage,
          });

          if (initialInstruction?.instructionId && initialInstruction.component !== 'success') {
            setLocalStorageInstruction(initialInstruction, jobsiteId, sessionId);
          }
        }

        setInstruction(initialInstruction);
      } catch (error) {
        console.error(error);
        handleApiError();
      }
    }
    fetchData();
  }, [currentLanguage, handleApiError, jobsiteId, requestInitialInstruction, sessionId]);

  return {
    instruction,
    handleNextInstructionRequest,
  };
}
